// Event-code format and local redemption bookkeeping (EVENT-CODES.md §3).
//
// Codes are minted server-side and are deliberately NOT derivable here: a
// client-side generator would let anyone print their own and kill the
// scarcity the fundraiser depends on. This module only parses, validates the
// shape, and records what the server said.

import type { Breed, GameState } from '../core/types';

/**
 * Crockford Base32: no 0/O/1/I, so a kid typing off a card can't confuse them.
 * Ten characters, grouped XXXX-XXX-XXX.
 */
export const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const CODE_LENGTH = 10;

const CROCKFORD_SUBSTITUTIONS: Record<string, string> = {
  O: '0',
  o: '0',
  I: '1',
  i: '1',
  L: '1',
  l: '1',
};

/**
 * Canonical form: uppercase, ambiguous letters folded to their digits,
 * separators dropped. `oooo-iii-lll` and `0000-111-111` are the same code.
 */
export function normalizeCode(raw: string): string {
  return [...raw.trim()]
    .map((char) => CROCKFORD_SUBSTITUTIONS[char] ?? char.toUpperCase())
    .filter((char) => CODE_ALPHABET.includes(char))
    .join('');
}

export function formatCode(normalized: string): string {
  const c = normalized;
  if (c.length !== CODE_LENGTH) return c;
  return `${c.slice(0, 4)}-${c.slice(4, 7)}-${c.slice(7, 10)}`;
}

export type CodeError = 'too_short' | 'too_long' | 'bad_characters' | 'already_redeemed';

export function validateCodeShape(raw: string): CodeError | null {
  // Anything outside the alphabet is dropped by normalize, so compare lengths
  // to catch a code that was mostly junk rather than mostly valid.
  const stripped = raw.trim().replace(/[\s-]/g, '');
  const normalized = normalizeCode(raw);
  if (normalized.length < CODE_LENGTH) {
    return stripped.length >= CODE_LENGTH ? 'bad_characters' : 'too_short';
  }
  if (normalized.length > CODE_LENGTH) return 'too_long';
  return null;
}

/** What the server returns for a good code (EVENT-CODES.md §3). */
export interface EggPayload {
  breed: Breed;
  tier: 'free' | 'premium';
  eventId: string;
  eventLabel: string;
  /** Guaranteed traits for this event bird, applied on hatch. */
  genetics?: {
    carriesBlueEgg?: boolean;
    silkieFeathered?: boolean;
    fiveToed?: boolean;
  };
  /** Accent colour for the bird's clay, so an event bird looks like one. */
  accentColor?: string;
  /**
   * Which life stage the bird arrives at. Absent means `egg` — every code
   * minted before grants existed was an egg, and must keep behaving like one.
   */
  stage?: 'egg' | 'chick' | 'pullet' | 'adult';
}

/**
 * What a code hands over. A bird is the default and the interesting case; the
 * other two exist because a card at a feria is not always a bird — sometimes it
 * is the coop that bird needs, or a pocket of Eggits to start with.
 */
export type GrantPayload =
  | ({ kind: 'bird' } & EggPayload)
  | { kind: 'item'; itemId: string; amount: number; eventId: string; eventLabel: string }
  | { kind: 'eggits'; amount: number; eventId: string; eventLabel: string };

/**
 * Reads whatever the server sent into a grant. Old-shaped responses (no `kind`)
 * are birds, because that is all a code could ever have been before now.
 */
export function grantFromResponse(raw: Record<string, unknown>): GrantPayload {
  const eventId = String(raw.eventId ?? '');
  const eventLabel = String(raw.eventLabel ?? '');
  const kind = raw.kind === 'item' || raw.kind === 'eggits' ? raw.kind : 'bird';

  if (kind === 'item') {
    return {
      kind,
      itemId: String(raw.itemId ?? ''),
      amount: Math.max(1, Number(raw.amount ?? 1)),
      eventId,
      eventLabel,
    };
  }
  if (kind === 'eggits') {
    return { kind, amount: Math.max(1, Number(raw.amount ?? 1)), eventId, eventLabel };
  }
  return {
    kind: 'bird',
    breed: raw.breed as Breed,
    tier: raw.tier === 'premium' ? 'premium' : 'free',
    eventId,
    eventLabel,
    genetics: (raw.genetics as EggPayload['genetics']) ?? {},
    accentColor: (raw.accentColor as string) ?? undefined,
    stage: (raw.stage as EggPayload['stage']) ?? 'egg',
  };
}

/** A code this device has already spent — the offline half of one-time use. */
export function isRedeemedLocally(state: GameState, code: string): boolean {
  return state.account.redeemedCodes.includes(normalizeCode(code));
}

/**
 * Records a redemption locally. The server is the authority on single use;
 * this stops an offline double-tap from queueing the same code twice.
 */
export function recordRedemption(state: GameState, code: string): void {
  const normalized = normalizeCode(code);
  if (!state.account.redeemedCodes.includes(normalized)) {
    state.account.redeemedCodes.push(normalized);
  }
}

/** Reads a code out of a scanned QR: https://clayfarm.online/r/XXXX-XXX-XXX */
export function codeFromUrl(url: string): string | null {
  const match = /\/r\/([0-9A-Za-z-]+)/.exec(url);
  if (!match) return null;
  const normalized = normalizeCode(match[1]);
  return normalized.length === CODE_LENGTH ? normalized : null;
}

/** Same, for the page the game itself was opened on. */
export function pendingCodeFromLocation(href = window.location.href): string | null {
  return codeFromUrl(href);
}
